import React from 'react';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import internalEndpoints from '@tcp/core/src/components/features/account/common/internalEndpoints';
import externalEndpoints from '@tcp/core/src/components/features/account/common/externalEndpoints';
import { getIsAccountPageRAR } from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { getLabelValue, isCanada } from '@tcp/core/src/utils/utils';
import { openWindow } from '@tcp/core/src/utils/utils.web';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Image from '@tcp/core/src/components/common/atoms/Image';
import { getIconPath, routerPush, getSessionStorage } from '@tcp/core/src/utils';
import withStyles from '../../../../../../common/hoc/withStyles';
import styles from '../styles/AccountDrawerBottomLinksNew.style';
import AccountDrawerLabels, {
  ACCOUNT_LABELS,
  CHECKOUT_LABELS,
  GLOBAL_LABELS,
} from '../../../views/AccountDrawerView/views/AccountDrawer.labels';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../../utils/labels/labelUtilty';

const LogOutPageContainer = dynamic(() =>
  // eslint-disable-next-line extra-rules/no-commented-out-code
  import(/* webpackChunkName: "logout-page" */ '../../../../Logout/container/LogOut.container')
);
const AccountDrawerBottomLinksNew = (props) => {
  const checkoutLabels = getLabelFromCopyOrFromTranslation(CHECKOUT_LABELS, AccountDrawerLabels);
  const accountLabels = getLabelFromCopyOrFromTranslation(ACCOUNT_LABELS, AccountDrawerLabels);
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, AccountDrawerLabels);
  const { className, labels, plccUser, showDashboardBottomLink, isPlacePerkEnabled, isRAREnabled } =
    props;
  /**
   * This function will handle click to go to respective links
   * @param {event, link, path} -
   */
  const onLinkRedirect = ({ e, link, path }) => {
    e.preventDefault();
    const { closedOverlay } = props;
    routerPush(link, path);
    if (!showDashboardBottomLink) closedOverlay();
  };

  const onExternalLinkRedirect = ({ e, link }) => {
    e.preventDefault();
    const { closedOverlay } = props;
    openWindow(link);
    closedOverlay();
  };

  const isCA = isCanada();
  const renderRewardsCreditCard = (toPath, asPath) => {
    const iconPath = showDashboardBottomLink
      ? getIconPath('my-place-rewards-icon')
      : getIconPath('credit-card-icon');
    const rewardsCardLabel = showDashboardBottomLink
      ? getDynamicLabelValues('dynamic.account_d.lbl_overview_myPlaceRewardsHeading')
      : getLabelValue(labels, 'CREATE_ACC_MY_PLACE_REWARDS_CC');

    return (
      !isCanada() && (
        <Anchor
          fontSizeVariation="large"
          fontFamily="secondary"
          to={toPath}
          asPath={asPath}
          onClick={(e) =>
            onLinkRedirect({
              e,
              link: toPath,
              path: asPath,
              showDashboardBottomLink,
            })
          }
          anchorVariation="primary"
          className="bottomLinks elem-pt-MED elem-pb-MED"
          dataLocator="rewards"
        >
          <div className="link-text-container">
            <div className="link-image">
              <Image alt="credit-card-icon" className="link-icon" src={iconPath} />
            </div>
            <span>{rewardsCardLabel}</span>
          </div>
        </Anchor>
      )
    );
  };

  const renderFavourite = () => {
    return (
      <Anchor
        fontSizeVariation="large"
        fontFamily="secondary"
        to={internalEndpoints.favorites.link}
        asPath={internalEndpoints.favorites.path}
        onClick={(e) =>
          onLinkRedirect({
            e,
            link: internalEndpoints.favorites.link,
            path: internalEndpoints.favorites.path,
            showDashboardBottomLink,
          })
        }
        anchorVariation="primary"
        className="bottomLinks elem-pt-MED elem-pb-MED "
        dataLocator="favorites"
      >
        <div className="link-text-container">
          <div className="link-image">
            <Image alt="my-favourites" className="link-icon" src={getIconPath('my-favourites')} />
          </div>
          <span>
            {renderLabel(
              accountLabels?.accountOverview?.lbl_overview_myFavoritesHeading,
              'lbl_overview_myFavoritesHeading'
            )}
          </span>
        </div>
      </Anchor>
    );
  };

  const renderPlacePerks = () => {
    return isPlacePerkEnabled ? (
      <Anchor
        fontSizeVariation="large"
        fontFamily="secondary"
        to={internalEndpoints.placePerks.link}
        asPath={internalEndpoints.placePerks.path}
        onClick={(e) =>
          onLinkRedirect({
            e,
            link: internalEndpoints.placePerks.link,
            path: internalEndpoints.placePerks.path,
            showDashboardBottomLink,
          })
        }
        anchorVariation="primary"
        className="bottomLinks elem-pt-MED elem-pb-MED "
        dataLocator="wallet"
      >
        <div className="link-text-container">
          <div className="link-image">
            <Image
              alt="place-perks"
              className="link-icon"
              src={getIconPath('place-perks-star-solid')}
            />
          </div>
          <span className="capFirstLetter">
            {renderLabel(
              accountLabels?.accountOverview?.lbl_placeperk_header,
              'lbl_placeperk_header'
            )}
          </span>
        </div>
      </Anchor>
    ) : null;
  };

  const LogOutLabel = renderLabel(
    globalLabels?.accountDrawer?.lbl_accountDrawer_LogOut,
    'lbl_accountDrawer_LogOut'
  );
  const isNewlyCreatedUser = getSessionStorage('isNewlyCreatedUser');
  const myPlaceRewardsToPath = internalEndpoints.placeRewardsPage.link;
  const myPlaceRewardsAsPath = internalEndpoints.placeRewardsPage.path;
  const myPlaceRewardsCreditCardToPath = internalEndpoints.rewardsCreditCard.link;
  const myPlaceRewardsCreditCardAsPath = internalEndpoints.rewardsCreditCard.path;
  return (
    <div className={className}>
      {showDashboardBottomLink && (
        <div className="accountDashboard">
          <div className="divider" />
          <div className="linksWrapper hideLinks">
            {renderFavourite()}
            {!isCA && !isNewlyCreatedUser && (
              <Anchor
                fontSizeVariation="large"
                fontFamily="secondary"
                onClick={(e) =>
                  onLinkRedirect({
                    e,
                    link: internalEndpoints.extraPointsPage.link,
                    path: internalEndpoints.extraPointsPage.path,
                    showDashboardBottomLink,
                  })
                }
                anchorVariation="primary"
                className="bottomLinks elem-pt-MED elem-pb-MED "
                dataLocator="earn-extra-point"
              >
                <div className="link-text-container">
                  <div className="link-image">
                    <Image
                      alt="earn-extra-point"
                      className="link-icon"
                      src={getIconPath('earning-points')}
                    />
                  </div>
                  <span>
                    {renderLabel(
                      accountLabels?.accountOverview?.lbl_overview_earnPointsHeading,
                      'lbl_overview_earnPointsHeading'
                    )}
                  </span>
                </div>
              </Anchor>
            )}
            {renderRewardsCreditCard(myPlaceRewardsToPath, myPlaceRewardsAsPath)}
            <Anchor
              fontSizeVariation="large"
              fontFamily="secondary"
              onClick={(e) =>
                onLinkRedirect({
                  e,
                  link: internalEndpoints.myPreferencesPage.link,
                  path: internalEndpoints.myPreferencesPage.path,
                  showDashboardBottomLink,
                })
              }
              target="_blank"
              anchorVariation="primary"
              className="bottomLinks elem-pt-MED elem-pb-MED "
              dataLocator="pay-my-bill"
              noLink
            >
              <div className="link-text-container">
                <div className="link-image">
                  <Image alt="pay-my-bill" className="link-icon" src={getIconPath('text-alerts')} />
                </div>
                <span>
                  {renderLabel(
                    accountLabels?.accountOverview?.lbl_overview_myPreferencesHeading,
                    'lbl_overview_myPreferencesHeading'
                  )}
                </span>
              </div>
            </Anchor>
            <Anchor
              fontSizeVariation="large"
              fontFamily="secondary"
              href="#"
              onClick={(e) =>
                onLinkRedirect({
                  e,
                  link: internalEndpoints.addressBook.link,
                  path: internalEndpoints.addressBook.path,
                  showDashboardBottomLink,
                })
              }
              anchorVariation="primary"
              className="bottomLinks elem-pt-MED elem-pb-MED "
              dataLocator="wallet"
            >
              <div className="link-text-container">
                <div className="link-image">
                  <Image alt="offers-wallet" className="link-icon" src={getIconPath('location')} />
                </div>
                <span>
                  {renderLabel(
                    globalLabels?.addEditAddress?.lbl_addEditAddress_addressBook,
                    'lbl_addEditAddress_addressBook'
                  )}
                </span>
              </div>
            </Anchor>
            {isRAREnabled && (
              <Anchor
                fontSizeVariation="large"
                fontFamily="secondary"
                href="#"
                onClick={(e) =>
                  onLinkRedirect({
                    e,
                    link: internalEndpoints.rateReview.link,
                    path: internalEndpoints.rateReview.path,
                    showDashboardBottomLink,
                  })
                }
                anchorVariation="primary"
                className="bottomLinks elem-pt-MED elem-pb-MED "
                dataLocator="wallet"
              >
                <div className="link-text-container">
                  <div className="link-image">
                    <Image
                      alt="offers-wallet"
                      className="link-icon"
                      src={getIconPath('rate-and-review')}
                    />
                  </div>
                  <span>
                    {renderLabel(accountLabels?.common?.lbl_rnr_main_title, 'lbl_rnr_main_title')}
                  </span>
                </div>
              </Anchor>
            )}
            <Anchor
              fontSizeVariation="large"
              fontFamily="secondary"
              href="#"
              onClick={(e) =>
                onLinkRedirect({
                  e,
                  link: internalEndpoints.buyItAgain.link,
                  path: internalEndpoints.buyItAgain.path,
                  showDashboardBottomLink,
                })
              }
              anchorVariation="primary"
              className="bottomLinks elem-pt-MED elem-pb-MED "
              dataLocator="wallet"
            >
              <div className="link-text-container">
                <div className="link-image">
                  <Image
                    alt="offers-wallet"
                    className="link-icon"
                    src={getIconPath('buy-it-again')}
                  />
                </div>
                <span>
                  {renderLabel(accountLabels?.common?.lbl_bia_main_title, 'lbl_bia_main_title')}
                </span>
              </div>
            </Anchor>
          </div>
          <div className="divider" />
        </div>
      )}
      {!showDashboardBottomLink && (
        <div className="accountDrawer">
          <div className="linksWrapper">
            <Anchor
              fontSizeVariation="large"
              fontFamily="secondary"
              to="/account?id=account-overview"
              asPath="/account"
              onClick={(e) =>
                onLinkRedirect({
                  e,
                  link: '/account?id=account-overview',
                  path: '/account',
                  showDashboardBottomLink,
                })
              }
              anchorVariation="primary"
              className="bottomLinks elem-pt-MED elem-pb-MED "
              dataLocator="account-overview"
            >
              <div className="link-text-container">
                <div className="link-image">
                  <Image alt="profile" className="link-icon" src={getIconPath('profile')} />
                </div>
                <span>
                  {renderLabel(
                    globalLabels?.accountDrawer?.lbl_account_dashboard,
                    'lbl_account_dashboard'
                  )}
                </span>
              </div>
            </Anchor>
            <Anchor
              fontSizeVariation="large"
              fontFamily="secondary"
              to={internalEndpoints.myOrderPage.link}
              asPath={internalEndpoints.myOrderPage.path}
              onClick={(e) =>
                onLinkRedirect({
                  e,
                  link: internalEndpoints.myOrderPage.link,
                  path: internalEndpoints.myOrderPage.path,
                  showDashboardBottomLink,
                })
              }
              anchorVariation="primary"
              className="bottomLinks elem-pt-MED elem-pb-MED "
              dataLocator="orders"
            >
              <div className="link-text-container">
                <div className="link-image">
                  <Image alt="orders" className="link-icon" src={getIconPath('orders')} />
                </div>
                <span>
                  {renderLabel(
                    globalLabels?.trackOrder?.lbl_trackOrder_header,
                    'lbl_trackOrder_header'
                  )}
                </span>
              </div>
            </Anchor>
            {renderRewardsCreditCard(
              myPlaceRewardsCreditCardToPath,
              myPlaceRewardsCreditCardAsPath
            )}
            {!isCA && plccUser && (
              <Anchor
                fontSizeVariation="large"
                to={externalEndpoints().plccPayMyBillPage}
                asPath={externalEndpoints().plccPayMyBillPage}
                onClick={(e) =>
                  onExternalLinkRedirect({
                    e,
                    link: externalEndpoints().plccPayMyBillPage,
                  })
                }
                target="_blank"
                anchorVariation="primary"
                className="bottomLinks elem-pt-MED elem-pb-MED "
                dataLocator="pay-my-bill"
              >
                <div className="link-text-container">
                  <div className="link-image">
                    <Image
                      alt="pay-my-bill"
                      className="link-icon"
                      src={getIconPath('pay-my-bill')}
                    />
                  </div>
                  <span>
                    {renderLabel(globalLabels?.accountDrawer?.lbl_payMyBill, 'lbl_payMyBill')}
                  </span>
                </div>
              </Anchor>
            )}
            {!isCA && (
              <Anchor
                fontSizeVariation="large"
                fontFamily="secondary"
                to={internalEndpoints.myWalletPage.link}
                asPath={internalEndpoints.myWalletPage.path}
                onClick={(e) =>
                  onLinkRedirect({
                    e,
                    link: internalEndpoints.myWalletPage.link,
                    path: internalEndpoints.myWalletPage.path,
                    showDashboardBottomLink,
                  })
                }
                anchorVariation="primary"
                className="bottomLinks elem-pt-MED elem-pb-MED "
                dataLocator="wallet"
              >
                <div className="link-text-container">
                  <div className="link-image">
                    <Image
                      alt="offers-wallet"
                      className="link-icon"
                      src={getIconPath('offers-wallet')}
                    />
                  </div>
                  <span>
                    {renderLabel(
                      checkoutLabels?.bagPage?.lbl_coupon_collapsible_header,
                      'lbl_coupon_collapsible_header'
                    )}
                  </span>
                </div>
              </Anchor>
            )}
            {renderPlacePerks()}
            {renderFavourite()}
            <div className="divider" />
          </div>
          <div className="link-container sign-out-wrapper">
            <LogOutPageContainer
              labels={labels}
              accountDrawer
              newLogoutLabelVal={LogOutLabel}
              isAccountDrawerNew
            />
          </div>
        </div>
      )}
    </div>
  );
};

AccountDrawerBottomLinksNew.propTypes = {
  className: PropTypes.string,
  labels: PropTypes.shape({}),
  plccUser: PropTypes.bool,
  showDashboardBottomLink: PropTypes.bool,
  closedOverlay: PropTypes.func.isRequired,
  isPlacePerkEnabled: PropTypes.bool,
};

AccountDrawerBottomLinksNew.defaultProps = {
  className: '',
  labels: {
    CREATE_ACC_MY_FAV: 'My Favorites',
    CREATE_ACC_MY_PLACE_REWARDS_CC: 'My Place Rewards Credit Card',
    CREATE_ACC_WALLET: 'Wallet',
    CREATE_ACC_ORDERS: 'Orders',
  },
  showDashboardBottomLink: false,
  isPlacePerkEnabled: false,
};

const mapStateToProps = (state) => ({
  isRAREnabled: getIsAccountPageRAR(state),
});

const AccountDrawerBottomLinksNewContainer = connect(
  mapStateToProps,
  null
)(AccountDrawerBottomLinksNew);
export default withStyles(AccountDrawerBottomLinksNewContainer, styles);
export { AccountDrawerBottomLinksNewContainer as AccountDrawerBottomLinksNewContainerVanilla };
